import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type ProjectSummary } from "../lib/api";
import type { Me } from "../lib/useMe";

export function ProjectsPage({ me }: { me: Me }) {
  const nav = useNavigate();
  const [projects, setProjects] = useState<(ProjectSummary & { organizationId?: string })[] | null>(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [preset, setPreset] = useState<"blank" | "ssx">("blank");
  const [startDate, setStartDate] = useState(() => nextMonday());
  const [file, setFile] = useState<File | null>(null);
  const [orgId, setOrgId] = useState(me.activeOrganizationId || me.organizations[0]?.organizationId || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.listProjects().then(setProjects).catch(e => setError(e.message)); }, []);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      let boardState: unknown;
      if (file) boardState = JSON.parse(await file.text());
      const { id } = await api.createProject({ name, preset, startDate, boardState, organizationId: orgId });
      nav(`/p/${id}`);
    } catch (err: any) {
      setError(err.message || String(err));
      setBusy(false);
    }
  };

  const teamName = (id?: string) => me.organizations.find(o => o.organizationId === id)?.name;

  return (
    <>
      <h1>Projects</h1>
      {error && <p className="error">{error}</p>}
      {!projects ? <p className="muted">Loading…</p> : projects.length === 0 ? (
        <p className="muted">No projects yet. Create one below.</p>
      ) : (
        <div className="grid">
          {projects.map(p => (
            <Link key={p.id} to={`/p/${p.id}`} className="card" style={{ color: "inherit", textDecoration: "none" }}>
              <div style={{ font: "600 18px 'Barlow Condensed'" }}>{p.name}</div>
              <div className="muted" style={{ fontSize: 13 }}>
                {me.organizations.length > 1 && teamName(p.organizationId) ? `${teamName(p.organizationId)} · ` : ""}
                updated {new Date(p.updatedAt).toLocaleString()}
              </div>
            </Link>
          ))}
        </div>
      )}

      <h2>New project</h2>
      <form className="card" onSubmit={create} style={{ maxWidth: 520 }}>
        <label className="field">Name<input required value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Entombed EA" /></label>
        {me.organizations.length > 1 && (
          <label className="field">Team
            <select value={orgId} onChange={e => setOrgId(e.target.value)}>
              {me.organizations.map(o => <option key={o.organizationId} value={o.organizationId}>{o.name}</option>)}
            </select>
          </label>
        )}
        <label className="field">Start from
          <select value={preset} onChange={e => setPreset(e.target.value as any)}>
            <option value="blank">Blank project</option>
            <option value="ssx">Steel Strix board (people, pods and the Aug 31 save)</option>
          </select>
        </label>
        {preset === "blank" && <label className="field">First workday<input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} /></label>}
        <label className="field">Board file (optional): a .json saved from the Schedule Board
          <input type="file" accept=".json,application/json" onChange={e => setFile(e.target.files?.[0] ?? null)} />
        </label>
        <button className="btn primary" disabled={busy || !name}>{busy ? "Creating…" : "Create project"}</button>
      </form>
    </>
  );
}

function nextMonday() {
  const d = new Date();
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
  return d.toISOString().slice(0, 10);
}
